import { useState } from "react";
import type { JSX, ReactNode } from "react";
import { Button, Input, Select, Tooltip } from "@fluentui/react-components";
import { ArrowClockwiseRegular, DeleteRegular, DismissRegular, EditRegular, PanelRightContractRegular, SaveRegular } from "@fluentui/react-icons";
import { motion, AnimatePresence } from "motion/react";
import type { Workbench } from "@minglun/domain";
import { type ConfirmRequest, type MigrationRequest, type WheelBasics, categories, useDialogFocus } from "../workbench-shared";
import { NumberInput, Field } from "./controls";

export function ExitDialog({
  saving,
  onSave,
  onDiscard,
  onCancel,
}: {
  saving: boolean;
  onSave(): void;
  onDiscard(): void;
  onCancel(): void;
}): JSX.Element {
  const dialogRef = useDialogFocus(true, onCancel);
  return (
    <div
      className="exit-dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving) onCancel();
      }}
    >
      <section
        ref={dialogRef}
        className="exit-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="exit-dialog-title"
        aria-describedby="exit-dialog-description"
        tabIndex={-1}
      >
        <div className="exit-dialog-icon">
          <SaveRegular />
        </div>
        <div className="exit-dialog-copy">
          <h2 id="exit-dialog-title">保存更改后退出？</h2>
          <p id="exit-dialog-description">
            当前有尚未保存的内容。保存可避免丢失本次修改。
          </p>
        </div>
        <div className="exit-dialog-actions">
          <Button
            appearance="primary"
            icon={<SaveRegular />}
            disabled={saving}
            onClick={onSave}
          >
            {saving ? "保存中" : "保存并退出"}
          </Button>
          <Button appearance="secondary" disabled={saving} onClick={onDiscard}>
            不保存退出
          </Button>
          <Button
            appearance="subtle"
            disabled={saving}
            data-dialog-autofocus
            onClick={onCancel}
          >
            取消
          </Button>
        </div>
      </section>
    </div>
  );
}

export function PresetNameDialog({
  title,
  fieldLabel,
  existingNames,
  initialName = "",
  confirmLabel = "保存预设",
  onCancel,
  onConfirm,
}: {
  title: string;
  fieldLabel: string;
  existingNames: string[];
  initialName?: string;
  confirmLabel?: string;
  onCancel(): void;
  onConfirm(name: string): void;
}): JSX.Element {
  const [name, setName] = useState(initialName);
  const normalized = name.trim();
  const duplicated = existingNames.some(
    (item) =>
      item.trim().toLocaleLowerCase() === normalized.toLocaleLowerCase(),
  );
  const dialogRef = useDialogFocus(true, onCancel);
  return (
    <div
      className="wheel-basics-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <motion.section
        ref={dialogRef}
        className="wheel-basics-dialog objective-preset-name-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="objective-preset-name-title"
        tabIndex={-1}
        initial={{ opacity: 0, scale: 0.985, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
      >
        <header>
          <h2 id="objective-preset-name-title">{title}</h2>
          <Tooltip content="取消" relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<DismissRegular />}
              aria-label={`取消${title}`}
              onClick={onCancel}
            />
          </Tooltip>
        </header>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            if (normalized && !duplicated) onConfirm(normalized);
          }}
        >
          <Field label={fieldLabel}>
            <Input
              autoFocus
              value={name}
              maxLength={40}
              aria-label={fieldLabel}
              aria-invalid={duplicated}
              onChange={(_event, data) => setName(data.value)}
            />
          </Field>
          <span
            className={`objective-preset-validation ${duplicated ? "error" : ""}`}
            role={duplicated ? "alert" : "status"}
          >
            {duplicated ? "已有同名预设" : `${normalized.length} / 40`}
          </span>
          <div className="wheel-basics-actions">
            <Button appearance="secondary" type="button" onClick={onCancel}>
              取消
            </Button>
            <Button
              appearance="primary"
              type="submit"
              disabled={!normalized || duplicated}
            >
              {confirmLabel}
            </Button>
          </div>
        </form>
      </motion.section>
    </div>
  );
}

export function WheelBasicsDialog({
  mode,
  initial,
  categories,
  onCancel,
  onConfirm,
}: {
  mode: "create" | "duplicate" | "edit";
  initial: WheelBasics;
  categories: Workbench["wheelCategories"];
  onCancel(): void;
  onConfirm(value: WheelBasics): void;
}): JSX.Element {
  const [draft, setDraft] = useState(initial);
  const valid =
    Boolean(draft.name.trim()) &&
    categories.some((item) => item.id === draft.wheelCategoryId) &&
    draft.maxStar >= 1;
  const dialogTitle =
    mode === "create"
      ? "新建关系命轮"
      : mode === "duplicate"
        ? "复制关系命轮"
        : "编辑基础信息";
  const confirmLabel =
    mode === "create"
      ? "创建并进入"
      : mode === "duplicate"
        ? "创建副本"
        : "确认修改";
  const dialogRef = useDialogFocus(true, onCancel);
  const submit = (): void => {
    if (valid) onConfirm({ ...draft, name: draft.name.trim() });
  };
  return (
    <div
      className="wheel-basics-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <motion.section
        ref={dialogRef}
        className="wheel-basics-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wheel-basics-title"
        tabIndex={-1}
        initial={{ opacity: 0, scale: 0.985, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.16, ease: [0.22, 1, 0.36, 1] }}
      >
        <header>
          <h2 id="wheel-basics-title">{dialogTitle}</h2>
          <Tooltip content="取消" relationship="label">
            <Button
              appearance="subtle"
              size="small"
              icon={<DismissRegular />}
              aria-label="取消基础设置"
              onClick={onCancel}
            />
          </Tooltip>
        </header>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <div className="wheel-basics-fields">
            <Field label="名称">
              <Input
                autoFocus
                value={draft.name}
                aria-label="关系命轮名称"
                onChange={(_event, data) =>
                  setDraft((current) => ({ ...current, name: data.value }))
                }
              />
            </Field>
            <Field label="类别">
              <Select
                value={draft.wheelCategoryId}
                aria-label="关系命轮类别"
                onChange={(_event, data) =>
                  setDraft((current) => ({
                    ...current,
                    wheelCategoryId: data.value,
                  }))
                }
              >
                {[...categories]
                  .sort((a, b) => a.order - b.order)
                  .map((item) => (
                    <option key={item.id} value={item.id}>
                      {item.name}
                    </option>
                  ))}
              </Select>
            </Field>
            <Field label="最高星">
              <NumberInput
                value={draft.maxStar}
                min={1}
                max={20}
                ariaLabel="关系命轮最高星"
                onChange={(maxStar) =>
                  setDraft((current) => ({ ...current, maxStar }))
                }
              />
            </Field>
          </div>
          <div className="wheel-basics-actions">
            <Button appearance="secondary" type="button" onClick={onCancel}>
              取消
            </Button>
            <Button appearance="primary" type="submit" disabled={!valid}>
              {confirmLabel}
            </Button>
          </div>
        </form>
      </motion.section>
    </div>
  );
}

export function ConfirmDialog({
  request,
  onClose,
}: {
  request: ConfirmRequest;
  onClose(): void;
}): JSX.Element {
  const dialogRef = useDialogFocus(true, onClose);
  return (
    <div
      className="confirm-dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <motion.section
        ref={dialogRef}
        className="confirm-dialog"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
        aria-describedby="confirm-dialog-description"
        tabIndex={-1}
        initial={{ opacity: 0, scale: 0.98, y: 6 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
      >
        <div className="confirm-dialog-icon">
          <DeleteRegular />
        </div>
        <div className="confirm-dialog-copy">
          <h2 id="confirm-dialog-title">{request.title}</h2>
          <p id="confirm-dialog-description">{request.message}</p>
        </div>
        <div className="confirm-dialog-actions">
          <Button
            className="confirm-danger"
            appearance="primary"
            onClick={() => {
              onClose();
              request.onConfirm();
            }}
          >
            {request.confirmLabel ?? "确认"}
          </Button>
          <Button
            appearance="secondary"
            data-dialog-autofocus
            onClick={onClose}
          >
            取消
          </Button>
        </div>
      </motion.section>
    </div>
  );
}

export function MigrationDialog({ request, onClose }: { request: MigrationRequest; onClose(): void }): JSX.Element {
  const dialogRef = useDialogFocus(true, onClose);
  return <div className="confirm-dialog-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
    <motion.section ref={dialogRef} className="confirm-dialog migration-dialog" role="alertdialog" aria-modal="true" aria-labelledby="migration-dialog-title" tabIndex={-1} initial={{ opacity: 0, scale: .98, y: 6 }} animate={{ opacity: 1, scale: 1, y: 0 }}>
      <div className="confirm-dialog-icon migration"><ArrowClockwiseRegular /></div>
      <div className="confirm-dialog-copy"><h2 id="migration-dialog-title">确认迁移 {request.count} 个{request.kind}</h2><p>导入内容已按当前命轮资料库自动升级。确认后才会写入，取消不会修改任何数据。</p></div>
      <div className="migration-summary"><span><strong>{request.defaultsAdded}</strong> 项新增内容采用默认值</span><span><strong>{request.remapped}</strong> 项已匹配到新版标识</span></div>
      {request.warnings.length > 0 && <div className="migration-warning-list" role="list"><strong>需要确认的差异</strong>{request.warnings.map((warning) => <p role="listitem" key={warning}>{warning}</p>)}</div>}
      <div className="confirm-dialog-actions"><Button appearance="secondary" onClick={onClose}>取消</Button><Button appearance="primary" onClick={() => { onClose(); request.onConfirm(); }}>确认迁移</Button></div>
    </motion.section>
  </div>;
}

export function DetailModal({
  open,
  title,
  headerContent,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  headerContent?: ReactNode;
  onClose(): void;
  children: ReactNode;
}): JSX.Element {
  const dialogRef = useDialogFocus(open, onClose);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="detail-modal-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.14 }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          <motion.section
            ref={dialogRef}
            className="detail-modal"
            role="dialog"
            aria-modal="true"
            aria-label={`${title}详情`}
            tabIndex={-1}
            initial={{ opacity: 0, scale: 0.985, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.99, y: 6 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          >
            <header className="detail-modal-header">
              <div className="detail-modal-heading">
                <h2>{title}</h2>
              </div>
              {headerContent && (
                <div className="detail-modal-header-content">
                  {headerContent}
                </div>
              )}
              <Tooltip content="关闭详情" relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<DismissRegular />}
                  data-dialog-autofocus
                  aria-label="关闭命轮详情"
                  onClick={onClose}
                />
              </Tooltip>
            </header>
            <div className="detail-modal-body">{children}</div>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function DetailDrawer({
  open,
  title,
  titleMeta,
  size = "default",
  headerContent,
  onTitleEdit,
  onClose,
  children,
}: {
  open: boolean;
  title: string;
  titleMeta?: string | undefined;
  size?: "default" | "wide";
  headerContent?: ReactNode;
  onTitleEdit?(): void;
  onClose(): void;
  children: ReactNode;
}): JSX.Element {
  const dialogRef = useDialogFocus(open, onClose);
  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="detail-drawer-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.14 }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) onClose();
          }}
        >
          <motion.aside
            ref={dialogRef}
            className={`detail-drawer ${size}`}
            role="dialog"
            aria-modal="true"
            aria-label={`${title}详情`}
            tabIndex={-1}
            initial={{ x: 42, opacity: 0.7 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: 42, opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          >
            <header
              className={`detail-drawer-header ${headerContent ? "has-content" : ""}`}
            >
              <div
                className={`detail-drawer-heading ${onTitleEdit ? "editable" : ""}`}
              >
                <div
                  className="detail-drawer-title-row"
                  onDoubleClick={onTitleEdit}
                  title={onTitleEdit ? "双击编辑名称、类别和最高星" : undefined}
                >
                  <h2>{title}</h2>
                  {titleMeta && (
                    <span className="detail-drawer-title-meta">
                      {titleMeta}
                    </span>
                  )}
                  {onTitleEdit && (
                    <Tooltip
                      content="编辑名称、类别和最高星"
                      relationship="label"
                    >
                      <button
                        type="button"
                        className="detail-title-edit"
                        aria-label="编辑名称、类别和最高星"
                        onClick={onTitleEdit}
                      >
                        <EditRegular />
                      </button>
                    </Tooltip>
                  )}
                </div>
              </div>
              {headerContent && (
                <div className="detail-drawer-header-content">
                  {headerContent}
                </div>
              )}
              <Tooltip content="隐藏详情" relationship="label">
                <Button
                  appearance="subtle"
                  size="small"
                  icon={<PanelRightContractRegular />}
                  aria-label="隐藏详情抽屉"
                  onClick={onClose}
                />
              </Tooltip>
            </header>
            <div className="detail-drawer-body">{children}</div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

